import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Select,
  Stack,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import axios from "../../../config/axiosConfig";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";

// FASE 2 de la Devolución (OT en "Asignado"): lo que quedó en "Espera" en la Fase 1
// (DevolucionEquiposOT.jsx) se clasifica acá a su condición final — igual que
// observacion.php/guarda2.php del sistema viejo, normalmente lo hace otro
// usuario/sesión distinta a quien armó la selección. Los accesorios NO entran acá: ya
// se devolvieron a stock directo en la Fase 1, sin estado intermedio.
//
// Sin botón propio de guardar: el botón "Concluir Devolución" (header de
// OrdenTrabajoPaso.jsx) llama a guardarClasificacion() vía ref y, si no hubo errores,
// recién ahí concluye la OT — un solo click hace las dos cosas.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };

const ESTADOS_FINALES = ["Inactivo", "Mantenimiento", "Baja", "Robado"];

const ClasificarDevolucionOT = forwardRef(({ cod_ot, esTKR }, ref) => {
  const { user } = useAuth();
  const [pendientes, setPendientes] = useState([]);
  const [accesorios, setAccesorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // { [key]: { estadoFinal, observacion } }
  const [clasificacion, setClasificacion] = useState({});
  const [errores, setErrores] = useState([]);
  const [equipoDialog, setEquipoDialog] = useState(null);

  const cargar = () => {
    setLoading(true);
    setError("");
    const peticiones = esTKR
      ? [axios.get("/api/historial-radio", { params: { cod_ot } }), axios.get("/api/historial-bateria", { params: { cod_ot } })]
      : [axios.get("/api/historial-varios", { params: { cod_ot } }), axios.get("/api/historial-chip", { params: { cod_ot } })];

    Promise.all([...peticiones, axios.get("/api/historial-accesorio", { params: { cod_ot } })])
      .then(([res1, res2, resAcc]) => {
        const equipos = esTKR
          ? [
              ...(res1.data || []).map((r) => ({ ...r, key: `radio-${r.id_historial}`, tipoEquipo: "Radio", tipoDialog: "radio", identificador: r.serie, equipoUrl: `/api/equipos-radio/${r.equipo_id}`, historialUrl: `/api/historial-radio/${r.id_historial}` })),
              ...(res2.data || []).map((r) => ({ ...r, key: `bateria-${r.id_historial}`, tipoEquipo: "Bateria", tipoDialog: "bateria", identificador: r.serie, equipoUrl: `/api/equipos-bateria/${r.equipo_id}`, historialUrl: `/api/historial-bateria/${r.id_historial}` })),
            ]
          : [
              ...(res1.data || []).map((r) => ({ ...r, key: `varios-${r.id_historial}`, tipoEquipo: r.tipo || "Varios", tipoDialog: "varios", identificador: r.serie, equipoUrl: `/api/equipos-varios/${r.equipo_id}`, historialUrl: `/api/historial-varios/${r.id_historial}` })),
              ...(res2.data || []).map((r) => ({ ...r, key: `chip-${r.id_historial}`, tipoEquipo: "Chip", tipoDialog: "chip", identificador: r.numero, equipoUrl: `/api/equipos-chip/${r.equipo_id}`, historialUrl: `/api/historial-chip/${r.id_historial}` })),
            ];
        const pendientesFiltrados = equipos.filter((r) => r.operacion === "Devolucion" && r.estado === "Espera");
        const accesoriosDevueltos = (resAcc.data || []).filter((r) => r.operacion === "Devolucion");
        setPendientes(pendientesFiltrados);
        setAccesorios(accesoriosDevueltos);
      })
      .catch((err) => {
        console.error("Error al cargar equipos pendientes de clasificar", err);
        setError("No se pudo cargar el equipo pendiente de clasificar.");
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [cod_ot, esTKR]);

  const setCampo = (key, campo, valor) => {
    setClasificacion((prev) => ({
      ...prev,
      [key]: { estadoFinal: "Inactivo", observacion: "", ...prev[key], [campo]: valor },
    }));
  };

  const clasificarEquipo = async (row) => {
    const datos = clasificacion[row.key];
    const estadoFinal = datos?.estadoFinal || "Inactivo";
    const observacionNueva = datos?.observacion || "";
    const observacionFinal = observacionNueva
      ? [row.observacion, observacionNueva].filter(Boolean).join(", ")
      : row.observacion || "";

    await axios.put(row.equipoUrl, { estado: estadoFinal });
    await axios.put(row.historialUrl, { estado: estadoFinal, observacion: observacionFinal, usu: user.username });
  };

  // Expuesto al padre (OrdenTrabajoPaso.jsx): clasifica todo lo pendiente y devuelve
  // los errores para que decida si conviene concluir la OT o no.
  useImperativeHandle(ref, () => ({
    guardarClasificacion: async () => {
      setErrores([]);
      const fallidos = [];

      for (const row of pendientes) {
        try {
          await clasificarEquipo(row);
        } catch (err) {
          fallidos.push(err?.response?.data?.error || `Error al clasificar "${row.identificador}"`);
        }
      }

      setErrores(fallidos);
      setClasificacion({});
      cargar();
      return { fallidos };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [pendientes, clasificacion]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Equipos Pendientes de Clasificar
        </Typography>
        <Chip size="small" color={pendientes.length ? "warning" : "success"} variant="outlined" label={`Pendientes: ${pendientes.length}`} sx={{ height: 20, fontSize: 11 }} />
      </Stack>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 360, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Tipo</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
              <TableCell sx={{ ...headCellSx, width: 140 }}>Estado Final</TableCell>
              <TableCell sx={headCellSx}>Observación</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!pendientes.length ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    No hay equipos pendientes de clasificar en esta OT.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              pendientes.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell>
                    <Box
                      component="span"
                      onClick={() => setEquipoDialog({ tipo: row.tipoDialog, identificador: row.identificador })}
                      sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                    >
                      {row.identificador}
                    </Box>
                  </TableCell>
                  <TableCell>{row.tipoEquipo}</TableCell>
                  <TableCell>{row.modelo}</TableCell>
                  <TableCell sx={{ verticalAlign: "middle" }}>
                    <Select
                      variant="outlined"
                      size="small"
                      value={clasificacion[row.key]?.estadoFinal || "Inactivo"}
                      onChange={(e) => setCampo(row.key, "estadoFinal", e.target.value)}
                      sx={{
                        fontSize: 12, width: "100%", bgcolor: "background.paper",
                        // Altura total FIJA (border-box) e idéntica a la del TextField de
                        // al lado — Select y TextField outlined no comparten el mismo
                        // padding por default, así que ajustar solo "py" en cada uno por
                        // separado los deja con alturas distintas y se ven desalineados.
                        "& .MuiSelect-select": {
                          height: 26, boxSizing: "border-box", display: "flex", alignItems: "center", py: 0,
                        },
                        // El radio de 14px del tema global queda desproporcionado en un
                        // input tan bajito (se ve "en píldora") — uno más chico acá.
                        // Ojo: en sx, un borderRadius NUMÉRICO se multiplica por
                        // theme.shape.borderRadius (14 acá) — hay que pasar un string
                        // con unidad explícita para que no vuelva a dar lo mismo.
                        "& .MuiOutlinedInput-notchedOutline": { borderRadius: "4px" },
                      }}
                    >
                      {ESTADOS_FINALES.map((op) => (
                        <MenuItem key={op} value={op} sx={{ fontSize: 11 }}>{op}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "middle" }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={clasificacion[row.key]?.observacion || ""}
                      onChange={(e) => setCampo(row.key, "observacion", e.target.value)}
                      fullWidth
                      sx={{
                        bgcolor: "background.paper",
                        // Mismo criterio que el Select de al lado: altura fija border-box.
                        "& .MuiInputBase-input": {
                          height: 26, boxSizing: "border-box", fontSize: 10.5, py: 0, display: "flex", alignItems: "center",
                        },
                        "& .MuiOutlinedInput-notchedOutline": { borderRadius: "4px" },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!!accesorios.length && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
            Accesorios Devueltos (ya en stock, sin clasificación pendiente)
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, maxHeight: 200, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Cantidad</TableCell>
                  <TableCell sx={headCellSx}>Fecha</TableCell>
                  <TableCell sx={headCellSx}>Observación</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accesorios.map((a) => (
                  <TableRow key={a.id} hover>
                    <TableCell>{a.modelo}</TableCell>
                    <TableCell>{a.cantidad}</TableCell>
                    <TableCell>{String(a.fecha || "").slice(0, 10)}</TableCell>
                    <TableCell>{a.observacion}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!!errores.length && (
        <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
          {errores.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </MuiAlert>
      )}

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
});

export default ClasificarDevolucionOT;
